import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import Seo from "../components/Seo";
import SectionEyebrow from "../components/SectionEyebrow";
import RevealOnScroll from "../components/RevealOnScroll";
import Button from "../components/Button";
import NewsletterForm from "../components/NewsletterForm";
import { newsArticles } from "../data/newsArticles";

const FORMATS = [
  {
    eyebrow: "● Positioning",
    title: "Manifesto",
    body:
      "Posizionamenti editoriali sullo stato del mercato sponsorship italiano. Una voce chiara su dove dovrebbe andare il settore.",
  },
  {
    eyebrow: "● Anatomia",
    title: "Inside the Deal",
    body:
      "Anatomia di un deal di sponsorship sportiva: budget, contenuti, KPI, durata, retorica. Per capire come ragionano property e brand.",
  },
  {
    eyebrow: "● Dati",
    title: "Sports Impact Observatory",
    body:
      "Analisi trimestrale del campionato Serie A: budget sponsorship, deal nuovi, deal chiusi, trend tematici. Output: 1 grafico + 1 verdetto per ogni voce.",
  },
  {
    eyebrow: "● Vocabolario",
    title: "Glossario",
    body:
      "Termini tecnici del nostro settore spiegati senza fluff. Da \"Cultural Impact\" a \"Activation Fee\", uno alla volta.",
  },
  {
    eyebrow: "● Attualità",
    title: "Impact Brief",
    body:
      "Brief settimanali su deal, lanci, campagne dello sport marketing globale: cosa è successo, cosa significa, cosa imparare.",
  },
  {
    eyebrow: "● Progetti",
    title: "Diario di Campo",
    body:
      "Behind the scenes dei progetti OnSports: scelte, errori, apprendimenti, KPI. Quello che di solito i case study finiti non raccontano.",
  },
];

const FAQS = [
  {
    q: "Con che frequenza arriva?",
    a: "Una newsletter ogni 2 settimane. Niente spam, niente notifiche extra.",
  },
  {
    q: "È a pagamento?",
    a: "No, è gratuita. Lo è sempre stata e lo rimarrà.",
  },
  {
    q: "Cosa fate con la mia email?",
    a: "La inseriamo nel nostro database newsletter (Salesflare). Non la condividiamo con terzi. Per dettagli, vedi la Privacy Policy.",
  },
  {
    q: "Posso disiscrivermi?",
    a: "Sì, in qualsiasi momento. Ogni email ha un link \"Cancella iscrizione\" in fondo.",
  },
];

const FormatCard = ({ eyebrow, title, body }) => (
  <div className="border border-galactico/15 p-6 md:p-7 h-full" data-testid={`format-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <p className="font-condensed uppercase tracking-widest text-[12px] text-lime mb-3">
      {eyebrow}
    </p>
    <h3 className="font-display text-[20px] md:text-[22px] leading-tight text-galactico mb-3">
      {title}
    </h3>
    <p className="text-[15px] leading-[1.6] text-galactico/75">{body}</p>
  </div>
);

const FaqItem = ({ q, a, open, onToggle, idx }) => (
  <div className="border-b border-galactico/15 py-5" data-testid={`faq-${idx}`}>
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-start justify-between gap-4 text-left group"
      aria-expanded={open}
    >
      <span className="font-display text-[18px] md:text-[22px] leading-snug text-galactico group-hover:text-lime transition-colors">
        {q}
      </span>
      <ChevronDown
        size={22}
        className={`shrink-0 mt-1 text-galactico/60 transition-transform ${open ? "rotate-180 text-lime" : ""}`}
        aria-hidden="true"
      />
    </button>
    {open && (
      <p className="mt-4 text-[15px] md:text-[16px] leading-[1.7] text-galactico/80 max-w-[60ch]">
        {a}
      </p>
    )}
  </div>
);

const Newsletter = () => {
  const [openFaq, setOpenFaq] = useState(0);
  const recent = (newsArticles || []).slice(0, 3);

  return (
    <>
      <Seo
        title="Newsletter OnSports: sponsorship sportiva italiana ogni 2 settimane"
        description="Iscriviti alla newsletter OnSports per ricevere Manifesti, Inside the Deal, Glossario, Sports Impact Observatory. Analisi data-driven della sponsorship sportiva italiana."
        path="/newsletter"
        image="/images/og-newsletter.jpg"
      />

      {/* SEZ. 1 — HERO */}
      <section
        className="bg-dodgers text-galactico pt-40 pb-20 md:pt-52 md:pb-24"
        data-testid="newsletter-hero"
      >
        <div className="container-os max-w-[860px]">
          <RevealOnScroll>
            <p className="font-condensed uppercase tracking-widest text-[13px] text-lime">
              ● Newsletter OnSports
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h1 className="display-h1 mt-6 max-w-[20ch]">
              La sponsorship sportiva italiana, decodificata. Ogni due{" "}
              <span className="text-lime">SETTIMANE.</span>
            </h1>
          </RevealOnScroll>
          <RevealOnScroll delay={0.14}>
            <p className="mt-8 text-[18px] md:text-[20px] leading-[1.65] text-galactico/85 max-w-[640px]">
              Manifesti, Inside the Deal, Glossario, Sports Impact Observatory.
              Niente fluff, solo analisi data-driven della sponsorship sportiva
              italiana. Pensata per chi lavora nel settore o lo vuole capire
              davvero.
            </p>
          </RevealOnScroll>
        </div>
      </section>

      {/* SEZ. 2 — IL FORM */}
      <section
        className="bg-galactico text-dodgers py-16 md:py-20"
        data-testid="newsletter-form-section"
      >
        <div className="container-os">
          <NewsletterForm variant="full" />
          <p className="mt-8 max-w-[480px] mx-auto text-center text-[13px] italic text-mandrogni leading-relaxed">
            Trattiamo i tuoi dati secondo la nostra{" "}
            <Link to="/privacy-policy" className="underline hover:text-dodgers">
              Privacy Policy
            </Link>
            . Puoi disiscriverti in qualsiasi momento.
          </p>
        </div>
      </section>

      {/* SEZ. 3 — COSA RICEVERAI */}
      <section
        className="bg-dodgers text-galactico py-16 md:py-24"
        data-testid="section-cosa-riceverai"
      >
        <div className="container-os">
          <div className="max-w-[760px] mb-12 md:mb-16">
            <RevealOnScroll>
              <SectionEyebrow>● Cosa riceverai</SectionEyebrow>
            </RevealOnScroll>
            <RevealOnScroll delay={0.06}>
              <h2 className="display-h2 mt-6 max-w-[22ch]">
                6 format editoriali, una cadenza chiara.
              </h2>
            </RevealOnScroll>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 max-w-[1100px]">
            {FORMATS.map((f, i) => (
              <RevealOnScroll key={f.title} delay={i * 0.05}>
                <FormatCard {...f} />
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* SEZ. 4 — DAGLI ULTIMI NUMERI */}
      <section
        className="bg-galactico text-dodgers py-16 md:py-24"
        data-testid="section-recent-articles"
      >
        <div className="container-os">
          <div className="max-w-[760px] mb-12">
            <RevealOnScroll>
              <SectionEyebrow>● Dagli ultimi numeri</SectionEyebrow>
            </RevealOnScroll>
            <RevealOnScroll delay={0.06}>
              <h2 className="font-display text-[28px] md:text-[36px] leading-tight mt-6 max-w-[28ch]">
                Hai un'idea di cosa riceverai. Ecco gli ultimi articoli pubblicati.
              </h2>
            </RevealOnScroll>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {recent.map((a) => (
              <Link
                key={a.slug}
                to={`/news/${a.slug}`}
                className="block border border-dodgers/15 p-6 hover:border-lime transition-colors group"
                data-testid={`recent-article-${a.slug}`}
              >
                <p className="font-condensed uppercase tracking-widest text-[12px] text-mandrogni mb-3">
                  {a.rubric}
                </p>
                <h3 className="font-display text-[20px] md:text-[22px] leading-tight group-hover:text-lime transition-colors">
                  {a.title}
                </h3>
                <span className="inline-block mt-4 font-condensed uppercase tracking-widest text-[12px] text-dodgers/60">
                  Leggi →
                </span>
              </Link>
            ))}
          </div>
          <div className="mt-10">
            <Button as={Link} to="/news" variant="secondary-dark" testId="newsletter-all-articles">
              Vedi tutti gli articoli
            </Button>
          </div>
        </div>
      </section>

      {/* SEZ. 5 — FAQ */}
      <section
        className="bg-dodgers text-galactico py-16 md:py-24"
        data-testid="section-newsletter-faq"
      >
        <div className="container-os max-w-[760px]">
          <RevealOnScroll>
            <p className="font-condensed uppercase tracking-widest text-[12px] text-lime">
              ● FAQ
            </p>
          </RevealOnScroll>
          <RevealOnScroll delay={0.06}>
            <h2 className="font-display text-[28px] md:text-[36px] leading-tight mt-6 mb-8">
              Le risposte rapide.
            </h2>
          </RevealOnScroll>
          <div>
            {FAQS.map((f, i) => (
              <FaqItem
                key={f.q}
                {...f}
                idx={i}
                open={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default Newsletter;
